import React, { useMemo } from 'react';
import { Box, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PercentIcon from '@mui/icons-material/Percent';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import { PieChart } from '@mui/x-charts/PieChart';
import { detectThemeSync } from '../../utils/themeUtils';

const ACCENT = '#4ce1b6';
const COMMISSION_COLOR = '#f59e0b';
const TAX_COLOR = '#8b5cf6';
const AFTER_COLOR = '#4ce1b6';

function formatMoney(amount, currency = 'INR') {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '—';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency || 'INR'} ${value.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
}

function SummaryStatCard({ label, value, hint, color, icon: Icon, isDark, loading }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2.5,
        width: '100%',
        height: '100%',
        minHeight: 132,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        border: `1px solid ${alpha(color, isDark ? 0.34 : 0.22)}`,
        background: isDark
          ? `linear-gradient(145deg, ${alpha(color, 0.16)} 0%, ${alpha('#0f172a', 0.55)} 100%)`
          : `linear-gradient(145deg, ${alpha(color, 0.1)} 0%, #ffffff 100%)`,
        boxShadow: isDark ? '0 10px 28px rgba(0,0,0,0.22)' : '0 10px 24px rgba(15,23,42,0.06)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: `0 16px 32px ${alpha(color, 0.18)}`,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
        <Box minWidth={0} sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: isDark ? '#94a3b8' : '#64748b',
              fontWeight: 800,
              letterSpacing: 0.7,
              textTransform: 'uppercase',
              display: 'block',
              minHeight: 18,
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width="80%" height={36} sx={{ mt: 0.4 }} />
          ) : (
            <Typography
              variant="h5"
              sx={{
                fontWeight: 900,
                mt: 0.35,
                color: isDark ? '#f8fafc' : '#0f172a',
                lineHeight: 1.2,
                minHeight: 32,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {value}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(color, 0.16),
            color,
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
      </Stack>
      {loading ? (
        <Skeleton variant="text" width="70%" height={20} />
      ) : (
        <Typography
          variant="caption"
          sx={{
            color: isDark ? '#94a3b8' : '#64748b',
            fontWeight: 600,
            minHeight: 32,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

export default function OtaCommissionSummary({ summary, loading, filtered = false }) {
  const isDark = detectThemeSync() === 'dark';
  const currency = summary?.currency || 'INR';
  const totalAmount = Number(summary?.total_amount || 0);
  const totalTax = Number(summary?.total_tax || 0);
  const totalCommission = Number(summary?.total_commission || 0);
  const afterCommission = Number(summary?.after_commission || Math.max(totalAmount - totalCommission, 0));
  const afterTaxAndCommission = Number(
    summary?.after_tax_and_commission || Math.max(totalAmount - totalTax - totalCommission, 0),
  );
  const bookingCount = Number(summary?.booking_count || 0);
  const hasTax = totalTax > 0.004;
  const commissionPercent = Number(summary?.commission_percent || 0);
  const taxPercent = Number(
    summary?.tax_percent || (totalAmount > 0 ? (totalTax / totalAmount) * 100 : 0),
  );
  const pieAfterValue = hasTax ? afterTaxAndCommission : afterCommission;
  const afterPercent = totalAmount > 0 ? (pieAfterValue / totalAmount) * 100 : 0;

  const pieSeries = useMemo(() => {
    if (totalAmount <= 0) {
      return [{ id: 'empty', value: 1, label: 'No data', color: isDark ? '#475569' : '#cbd5e1' }];
    }
    const slices = [];
    if (hasTax) {
      slices.push({ id: 'tax', value: totalTax, label: 'Tax', color: TAX_COLOR });
    }
    slices.push({ id: 'commission', value: totalCommission, label: 'Commission', color: COMMISSION_COLOR });
    slices.push({
      id: 'after',
      value: pieAfterValue,
      label: hasTax ? 'After tax & commission' : 'After commission',
      color: AFTER_COLOR,
    });
    return slices;
  }, [hasTax, isDark, pieAfterValue, totalAmount, totalCommission, totalTax]);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2.5,
        p: { xs: 2, md: 2.5 },
        borderRadius: 3,
        border: `1px solid ${alpha(ACCENT, isDark ? 0.22 : 0.2)}`,
        background: isDark
          ? `linear-gradient(160deg, ${alpha('#0f172a', 0.92)} 0%, ${alpha('#042018', 0.88)} 100%)`
          : `linear-gradient(160deg, #ffffff 0%, ${alpha(ACCENT, 0.08)} 100%)`,
        boxShadow: isDark ? '0 16px 40px rgba(0,0,0,0.28)' : '0 12px 32px rgba(15,23,42,0.06)',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.25}
        mb={2}
      >
        <Box>
          <Typography
            variant="caption"
            sx={{ color: ACCENT, fontWeight: 800, letterSpacing: 1.1, display: 'block' }}
          >
            OTA MIX
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 900, color: isDark ? '#f8fafc' : '#0f172a' }}>
            {hasTax ? 'Commission, tax & total' : 'Commission vs total amount'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            size="small"
            label={loading ? 'Updating…' : `${bookingCount} booking${bookingCount === 1 ? '' : 's'}`}
            sx={{
              fontWeight: 700,
              bgcolor: alpha(ACCENT, isDark ? 0.16 : 0.14),
              color: isDark ? '#e2e8f0' : '#042018',
            }}
          />
          {filtered ? (
            <Chip
              size="small"
              label="Filtered"
              sx={{
                fontWeight: 800,
                bgcolor: alpha(COMMISSION_COLOR, 0.16),
                color: isDark ? '#fde68a' : '#92400e',
              }}
            />
          ) : null}
        </Stack>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(280px, 340px) 1fr' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, md: 2 },
            borderRadius: 2.5,
            border: `1px solid ${alpha(ACCENT, isDark ? 0.14 : 0.18)}`,
            bgcolor: isDark ? alpha('#0f172a', 0.45) : alpha('#ffffff', 0.82),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 260, md: 300 },
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 280,
              height: 240,
              position: 'relative',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {loading ? (
              <Skeleton variant="circular" width={188} height={188} />
            ) : (
              <>
                <PieChart
                  width={280}
                  height={240}
                  margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                  series={[
                    {
                      data: pieSeries,
                      innerRadius: 62,
                      outerRadius: 98,
                      cx: 140,
                      cy: 120,
                      cornerRadius: 8,
                      paddingAngle: 3,
                      arcLabel: (item) => {
                        if (item.id === 'empty' || totalAmount <= 0) return '';
                        const pct = (item.value / totalAmount) * 100;
                        return pct >= 8 ? `${pct.toFixed(1)}%` : '';
                      },
                      arcLabelMinAngle: 18,
                      valueFormatter: (item) =>
                        item.id === 'empty' ? 'No data' : `${item.label}: ${formatMoney(item.value, currency)}`,
                    },
                  ]}
                  slotProps={{ legend: { hidden: true } }}
                  sx={{
                    maxWidth: '100%',
                    '& .MuiPieArcLabel-root': {
                      fill: isDark ? '#f8fafc' : '#0f172a',
                      fontWeight: 800,
                      fontSize: 12,
                    },
                    '& .MuiPieArc-root': {
                      filter: isDark
                        ? 'drop-shadow(0 4px 10px rgba(0,0,0,0.45))'
                        : 'drop-shadow(0 4px 8px rgba(15,23,42,0.12))',
                    },
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'grid',
                    placeItems: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <Box textAlign="center">
                    <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
                      Commission share
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 900, color: ACCENT, lineHeight: 1.15 }}>
                      {commissionPercent.toFixed(1)}%
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
          <Stack direction="row" spacing={2} mt={0.5} flexWrap="wrap" justifyContent="center">
            {[
              ...(hasTax ? [{ label: 'Tax', color: TAX_COLOR, pct: taxPercent }] : []),
              { label: 'Commission', color: COMMISSION_COLOR, pct: commissionPercent },
              {
                label: hasTax ? 'After tax & commission' : 'After commission',
                color: AFTER_COLOR,
                pct: afterPercent,
              },
            ].map((item) => (
              <Stack key={item.label} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: isDark ? '#cbd5e1' : '#475569' }}>
                  {item.label} {loading ? '' : `${item.pct.toFixed(1)}%`}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gridAutoRows: { sm: '1fr' },
            gap: 1.5,
            alignItems: 'stretch',
            height: '100%',
            minHeight: { md: 300 },
          }}
        >
          <SummaryStatCard
            label="Total amount"
            value={formatMoney(totalAmount, currency)}
            hint="Sum of booking totals in this filter"
            color="#6366f1"
            icon={AccountBalanceWalletOutlinedIcon}
            isDark={isDark}
            loading={loading}
          />
          {hasTax ? (
            <SummaryStatCard
              label="Total tax"
              value={formatMoney(totalTax, currency)}
              hint={`${taxPercent.toFixed(1)}% of total amount`}
              color={TAX_COLOR}
              icon={ReceiptLongOutlinedIcon}
              isDark={isDark}
              loading={loading}
            />
          ) : null}
          <SummaryStatCard
            label="Total OTA commission"
            value={formatMoney(totalCommission, currency)}
            hint={`${commissionPercent.toFixed(1)}% of total amount`}
            color={COMMISSION_COLOR}
            icon={PercentIcon}
            isDark={isDark}
            loading={loading}
          />
          <SummaryStatCard
            label={hasTax ? 'After tax & commission' : 'After commission'}
            value={formatMoney(hasTax ? afterTaxAndCommission : afterCommission, currency)}
            hint={hasTax ? 'Total minus tax and OTA commission' : 'Total amount minus OTA commission'}
            color={AFTER_COLOR}
            icon={SavingsOutlinedIcon}
            isDark={isDark}
            loading={loading}
          />
        </Box>
      </Box>
    </Paper>
  );
}
